<div class="w-full h-full min-h-0 flex flex-col overflow-hidden p-2">
  <section class="bg-white rounded-md shadow-custom-2 p-4 flex flex-col gap-4 flex-1 min-h-0 overflow-hidden">
    <div class="w-full flex items-center justify-between">
        <button class="w-10 h-10 flex items-center justify-center hover:text-nside-orange-400"
          (click)="$event.preventDefault(); onBack();"
        >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-left-icon lucide-arrow-left"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
        </button>
        <h2 class="oswald-font uppercase text-2xl text-nside-orange-400">Physical Security</h2>
        <div class="w-10 h-10"></div>
    </div>
    <div class="w-full overflow-x-auto overflow-y-hidden pb-1">
      <ol class="flex items-center min-w-max">
        @for (section of definition.sections; track section.key; let sectionIndex = $index; let isLast = $last) {
          <li class="flex items-center">
            <button
              type="button"
              [attr.data-tooltip-target]="'section-tooltip-' + sectionIndex"
              data-tooltip-placement="bottom"
              class="flex items-center justify-center w-10 h-10 rounded-full shrink-0 transition-colors"
              [class.bg-nside-orange-400]="currentSectionIndex === sectionIndex"
              [class.text-white]="currentSectionIndex === sectionIndex"
              [class.bg-orange-100]="currentSectionIndex !== sectionIndex && canProceedFromSection(section)"
              [class.text-nside-orange-400]="currentSectionIndex !== sectionIndex && canProceedFromSection(section)"
              [class.bg-gray-100]="!canProceedFromSection(section) && currentSectionIndex !== sectionIndex"
              [class.text-gray-500]="!canProceedFromSection(section) && currentSectionIndex !== sectionIndex"
              [class.cursor-not-allowed]="!canMoveToSection(sectionIndex)"
              [disabled]="!canMoveToSection(sectionIndex)"
              [title]="section.title"
              (click)="goToSection(sectionIndex)"
            >
              {{ sectionIndex + 1 }}
            </button>
            <div
              [id]="'section-tooltip-' + sectionIndex"
              role="tooltip"
              class="absolute z-30 invisible inline-block px-3 py-2 text-sm font-medium text-white bg-gray-600 rounded-lg shadow-sm opacity-0 tooltip"
            >
              {{ section.title }}
              <div class="tooltip-arrow" data-popper-arrow></div>
            </div>
            @if (!isLast) {
              <div class="min-w-12 h-1 border-b-4 border-gray-100 shrink-0"></div>
            }
          </li>
        }
      </ol>
    </div>

    <div class="flex items-center justify-between gap-4">
      <h2 class="text-lg font-semibold text-slate-800">{{ currentSection.title }}</h2>
      <span class="text-xs font-medium rounded px-2 py-1 bg-slate-100 text-slate-600 uppercase">
        {{ currentSectionProgressLabel }}
      </span>
    </div>

    <div #scrollContainer class="flex-1 overflow-y-auto pr-1 flex flex-col gap-5">
      @for (field of getVisibleFields(currentSection); track field.key; let fieldIndex = $index) {
        <article class="border border-slate-200 rounded-md p-4 flex flex-col gap-3">
          <div class="flex flex-col gap-1">
            <h3 class="font-semibold text-slate-800">{{ fieldIndex + 1 }}. {{ field.label }}</h3>
            @if (field.description) {
              <p class="text-sm text-slate-500">{{ field.description }}</p>
            }
          </div>

          @switch (field.type) {
            @case ('radio') {
              <div class="flex flex-wrap gap-2" [formGroup]="form">
                @for (option of getRadioOptions(field); track option.value) {
                  <label
                    class="block cursor-pointer rounded-lg border p-3 transition-colors flex-1 min-w-32"
                    [class.border-orange-500]="getFieldControl(field.key).value === option.value"
                    [class.bg-orange-50]="getFieldControl(field.key).value === option.value"
                    [class.border-slate-200]="getFieldControl(field.key).value !== option.value"
                    [class.bg-white]="getFieldControl(field.key).value !== option.value"
                  >
                    <div class="flex items-start gap-3">
                      <input
                        type="radio"
                        [formControlName]="field.key"
                        [value]="option.value"
                        [attr.name]="field.key"
                        [attr.id]="field.key + '-' + option.value"
                        class="mt-1 h-4 w-4 border-slate-300 text-orange-600 focus:ring-orange-500"
                      />
                      <div class="flex flex-col gap-1">
                        <span class="font-semibold text-slate-800">{{ formatOptionValue(option.value) }}</span>
                        @if (option.label !== option.value) {
                          <span class="text-xs leading-5 text-slate-700">{{ option.label }}</span>
                        }
                      </div>
                    </div>
                  </label>
                }
              </div>
            }

            @case ('input') {
              <input
                type="text"
                [formControl]="getTextControl(field.key)"
                class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            }

            @case ('phone') {
              <input
                type="tel"
                [formControl]="getTextControl(field.key)"
                inputmode="numeric"
                maxlength="14"
                (input)="handlePhoneInput(field.key, $event)"
                class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              />
            }

            @case ('textarea') {
              <textarea
                rows="4"
                [formControl]="getTextControl(field.key)"
                class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              ></textarea>
            }

            @case ('address') {
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3" [formGroup]="getAddressGroup(field.key)">
                <label class="flex flex-col gap-1 md:col-span-2">
                  <span class="text-sm text-slate-600">Address line 1</span>
                  <input type="text" formControlName="address_one" class="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                </label>
                <label class="flex flex-col gap-1 md:col-span-2">
                  <span class="text-sm text-slate-600">Address line 2</span>
                  <input type="text" formControlName="address_two" class="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                </label>
                <label class="flex flex-col gap-1">
                  <span class="text-sm text-slate-600">City</span>
                  <input type="text" formControlName="city" class="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                </label>
                <div class="grid grid-cols-2 gap-3">
                  <label class="flex flex-col gap-1">
                    <span class="text-sm text-slate-600">State</span>
                    <select formControlName="state" class="rounded-md border border-slate-300 px-3 py-2 text-sm bg-white">
                      <option value="">Select state</option>
                      @for (state of states; track state) {
                        <option [value]="state.value">{{ state.label }}</option>
                      }
                    </select>
                  </label>
                  <label class="flex flex-col gap-1">
                    <span class="text-sm text-slate-600">ZIP code</span>
                    <input type="text" inputmode="numeric" maxlength="9" formControlName="zipcode" class="rounded-md border border-slate-300 px-3 py-2 text-sm" />
                  </label>
                </div>
              </div>
            }

            @case ('multicheckbox') {
              <div class="grid grid-cols-1 md:grid-cols-2 gap-2" [formGroup]="getCheckboxGroup(field.key)">
                @for (optionKey of checkboxOptionKeys(field); track optionKey) {
                  <label class="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                    <input
                      type="checkbox"
                      [formControlName]="optionKey"
                      class="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                    />
                    <span class="text-sm text-slate-700">{{ getOptionLabel(field, optionKey) }}</span>
                  </label>
                }
              </div>
            }

            @case ('repeat') {
              <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-2">
                  @for (control of getRepeatArray(field.key).controls; track $index; let itemIndex = $index) {
                    <div class="flex items-center gap-2">
                      <input
                        type="text"
                        [formControl]="$any(control)"
                        class="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      />
                      <button
                        type="button"
                        class="px-3 py-2 rounded-md border border-slate-300 text-sm text-slate-700"
                        (click)="removeRepeatItem(field.key, itemIndex)"
                      >
                        Remove
                      </button>
                    </div>
                  }
                </div>
                <div>
                  <button
                    type="button"
                    class="px-4 py-2 rounded-md border border-nside-orange-200 text-sm font-medium text-nside-orange-400"
                    (click)="addRepeatItem(field.key)"
                  >
                    Add Item
                  </button>
                </div>
              </div>
            }
          }
        </article>
      }
    </div>

    <div class="text-sm flex items-center justify-between pt-2">
      <button
        type="button"
        class="px-4 py-1.5 w-20 rounded-md border border-nside-gray-400 oswald-font text-slate-700 disabled:opacity-40"
        [disabled]="isFirstSection"
        (click)="previousSection()"
      >
        Back
      </button>

      @if (!isLastSection) {
        <button
          type="button"
          class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
          [ngClass]="{
            'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !canProceedFromSection(currentSection),
            'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': canProceedFromSection(currentSection)
          }"
          [disabled]="!canProceedFromSection(currentSection)"
          (click)="nextSection()"
        >
          Next
        </button>
      }

      @if (isLastSection) {
        <button
          type="button"
          class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white"
          [ngClass]="{
            'border-nside-gray-100 from-nside-gray-100 to-nside-gray-200 cursor-not-allowed': !canProceedFromSection(currentSection),
            'border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200': canProceedFromSection(currentSection)
          }"
          [disabled]="!canProceedFromSection(currentSection)"
          (click)="submit()"
        >
          Submit
        </button>
      }
    </div>
  </section>
</div>

@if (loading()) {
  <spinner pageName="physical-security"></spinner>
}

<popup-message
  [type]="messageModal()"
  [message]="popupMessage()"
  (close)="resetMessageModal()"
></popup-message>
